import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { EyeOff, Eye, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Ctx = createContext(null);

export function DashPrefsProvider({ children }) {
  const [hidden, setHidden] = useState([]);
  const [labels, setLabels] = useState({});
  useEffect(() => { api.get("/me/dashboard-prefs").then((r) => setHidden(r.data.hidden || [])).catch(() => {}); }, []);
  const save = useCallback((next) => {
    setHidden(next);
    api.put("/me/dashboard-prefs", { hidden: next }).catch(() => toast.error("Einstellung konnte nicht gespeichert werden"));
  }, []);
  const register = useCallback((id, label) => setLabels((l) => (l[id] === label ? l : { ...l, [id]: label })), []);
  return <Ctx.Provider value={{ hidden, labels, save, register }}>{children}</Ctx.Provider>;
}

export function DashCard({ id, label, children }) {
  const { hidden, save, register } = useContext(Ctx);
  useEffect(() => { register(id, label); }, [id, label, register]);
  if (hidden.includes(id)) return null;
  return (
    <div className="dash-card relative group" data-testid={`dash-card-${id}`}>
      <button
        onClick={() => { save([...hidden, id]); toast.success(`„${label}“ ausgeblendet`); }}
        className="absolute -top-3 -right-3 z-10 p-1.5 rounded-full shadow-sm bg-white/90 border border-slate-200 text-slate-400 hover:text-slate-900 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        title={`„${label}“ ausblenden`} data-testid={`dash-hide-${id}`}
      >
        <EyeOff size={13} />
      </button>
      <span className="absolute -top-2.5 right-6 z-10 bg-white rounded-full" ><InfoTip k={id} testId={`dash-info-${id}`} className="!w-5 !h-5 bg-white" /></span>
      <div className="dash-body">{children}</div>
    </div>
  );
}

export function HiddenCardsBar() {
  const { hidden, labels, save } = useContext(Ctx);
  const [open, setOpen] = useState(false);
  const known = hidden.filter((id) => labels[id]);
  if (!known.length) return null;
  return (
    <div className="mb-6 border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-xs text-slate-600" data-testid="dash-hidden-bar">
      <div className="flex flex-wrap items-center gap-3">
        <EyeOff size={13} />
        <span>{known.length} {known.length === 1 ? "Karte" : "Karten"} ausgeblendet</span>
        <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-slate-900" data-testid="dash-hidden-toggle">
          Ausgeblendete anzeigen <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button onClick={() => save(hidden.filter((id) => !labels[id]))} className="ml-auto underline underline-offset-2 hover:text-slate-900" data-testid="dash-show-all">Alle einblenden</button>
      </div>
      {open && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          {known.map((id) => (
            <button key={id} onClick={() => save(hidden.filter((x) => x !== id))} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-2.5 py-1 hover:border-slate-900 transition-colors" data-testid={`dash-show-${id}`}>
              <Eye size={12} /> {labels[id]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
